import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { MessageDescriptor, useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, SearchField } from "@mapmycustomers/ui";

import { connectToMailNylas } from "@app/scene/settings/util/connectToNylas";
import { isNylasConnectionActive } from "@app/store/nylas";
import { RootState } from "@app/store/rootReducer";

import { searchParentEmailThreads } from "../store/actions";
import { getEmailThreadsEntity } from "../store/selectors";
import canSyncEntityEmails from "../util/canSyncEntityEmails";
import useParentEmailThreadsFolderAnalytics from "../util/useParentEmailThreadsFolderAnalytics";

import EmailSyncAlert from "./EmailSyncAlert";
import messages from "./messages";
import styles from "./ParentEmails.module.scss";
import ParentEmailsList from "./ParentEmailsList";

interface ParentEmailsProps {
  entity: EntitiesSupportedByEmailFeature;
  nylasActive: boolean;
  onSearch: typeof searchParentEmailThreads.request;
}

const ParentEmails: React.FC<ParentEmailsProps> = ({ entity, nylasActive, onSearch }) => {
  const intl = useIntl();
  const analytics = useParentEmailThreadsFolderAnalytics();

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (nylasActive && canSyncEntityEmails(entity)) {
      onSearch();
    }
  }, [entity, nylasActive, onSearch]);

  const handleSearch: typeof searchParentEmailThreads.request = useDebouncedCallback(
    [onSearch, 300],
    [onSearch]
  );

  const handleSearchTextChange = useCallback(
    (searchText: string) => {
      setSearchText(searchText);
      if (!searchText.length || searchText.length >= 3) {
        handleSearch({ searchText });
        analytics.completed("Search");
      }
    },
    [analytics, handleSearch]
  );

  const renderContent = () => {
    if (!nylasActive) {
      return (
        <div className={styles.syncEmailMessageContainer}>
          <div className={styles.syncEmailMessage}>
            {intl.formatMessage(messages.syncEmailMessage)}
          </div>
          <Button onClick={connectToMailNylas} type="primary">
            {intl.formatMessage(messages.syncEmailButtonLabel)}
          </Button>
        </div>
      );
    }

    if (!canSyncEntityEmails(entity)) {
      let message: MessageDescriptor;
      switch (entity.entity) {
        case EntityType.COMPANY:
          message = messages.companyEmailsCantBeSynced;
          break;
        case EntityType.PERSON:
          message = messages.personEmailsCantBeSyncedMessage;
      }

      return (
        <div className={styles.entityEmailsCantBeSyncedMessage}>{intl.formatMessage(message)}</div>
      );
    }

    return (
      <>
        <EmailSyncAlert />
        <ParentEmailsList />
      </>
    );
  };

  return (
    <div className={styles.container}>
      <SearchField
        disabled={!nylasActive || !entity}
        onChange={handleSearchTextChange}
        placeholder={intl.formatMessage(messages.searchTextPlaceholder)}
        size="large"
        value={searchText}
      />
      {renderContent()}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entity: getEmailThreadsEntity(state)!,
  nylasActive: isNylasConnectionActive(state),
});

const mapDispatchToProps = {
  onSearch: searchParentEmailThreads.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ParentEmails);
